/**
 * Custom URL parameter names to avoid adblocker stripping
 * These map to standard UTM parameters for partner integrations
 *
 * Partners should use these parameter names in their links:
 * - ref: Traffic source (utm_source equivalent)
 * - via: Traffic medium (utm_medium equivalent)
 * - cmp: Campaign identifier (utm_campaign equivalent)
 * - var: Content variant (utm_content equivalent)
 * - kw: Keyword/term (utm_term equivalent)
 *
 * Example: https://start.crowdsec.net?ref=partner_site&via=email&cmp=spring_2025
 */
export const UTM_PARAM_MAPPING = {
  source: "ref", // utm_source → ref (reference/referrer)
  medium: "via", // utm_medium → via (channel)
  campaign: "cmp", // utm_campaign → cmp (campaign)
  content: "var", // utm_content → var (variant)
  term: "kw", // utm_term → kw (keyword)
} as const;

// Function to add custom tracking parameters to URLs
function addTrackingParameters(
  url: string,
  trackingParams: {
    source?: string;
    medium?: string;
    campaign?: string;
    content?: string;
    term?: string;
  },
): string {
  if (!url || url.trim() === "") return url;

  try {
    const urlObj = new URL(url);

    // Use our custom parameter names instead of utm_* to avoid adblockers
    if (trackingParams.source)
      urlObj.searchParams.set(UTM_PARAM_MAPPING.source, trackingParams.source);
    if (trackingParams.medium)
      urlObj.searchParams.set(UTM_PARAM_MAPPING.medium, trackingParams.medium);
    if (trackingParams.campaign)
      urlObj.searchParams.set(
        UTM_PARAM_MAPPING.campaign,
        trackingParams.campaign,
      );
    if (trackingParams.content)
      urlObj.searchParams.set(
        UTM_PARAM_MAPPING.content,
        trackingParams.content,
      );
    if (trackingParams.term)
      urlObj.searchParams.set(UTM_PARAM_MAPPING.term, trackingParams.term);

    return urlObj.toString();
  } catch (error) {
    // If URL is invalid, return original
    // eslint-disable-next-line no-console
    console.warn("Invalid URL for tracking parameters:", url, error);

    return url;
  }
}

/**
 * Extract tracking parameters from URL
 * Converts our custom param names back to standard UTM format for internal use
 */
export function extractTrackingParams(searchParams: URLSearchParams): {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
} {
  return {
    utm_source: searchParams.get(UTM_PARAM_MAPPING.source) || undefined,
    utm_medium: searchParams.get(UTM_PARAM_MAPPING.medium) || undefined,
    utm_campaign: searchParams.get(UTM_PARAM_MAPPING.campaign) || undefined,
    utm_content: searchParams.get(UTM_PARAM_MAPPING.content) || undefined,
    utm_term: searchParams.get(UTM_PARAM_MAPPING.term) || undefined,
  };
}

const SESSION_TIMEOUT_MS = 30 * 60 * 1000;

function getSessionInfo() {
  if (typeof window === "undefined")
    return { userId: "anonymous", sessionId: "anonymous", engagementTime: 100 };

  let userId = localStorage.getItem("crowdsec_user_id");
  let sessionId = localStorage.getItem("crowdsec_session_id");
  const lastActivity = localStorage.getItem("crowdsec_last_activity");
  const currentTime = Date.now();

  // Générer un userId si il n'existe pas
  if (!userId) {
    userId = `user_${currentTime}_${Math.random().toString(36).substring(2, 9)}`;
    localStorage.setItem("crowdsec_user_id", userId);
  }

  if (
    !sessionId ||
    !lastActivity ||
    currentTime - parseInt(lastActivity, 10) > SESSION_TIMEOUT_MS
  ) {
    sessionId = (Date.now() + Math.floor(Math.random() * 1000)).toString();
    localStorage.setItem("crowdsec_session_id", sessionId);
  }
  localStorage.setItem("crowdsec_last_activity", currentTime.toString());

  return { userId, sessionId, engagementTime: 100 };
}

export async function setUserAction({
  eventType,
  questionId,
  value,
}: {
  eventType: string;
  questionId: string;
  value: string | string[];
}) {
  try {
    const { userId, sessionId, engagementTime } = getSessionInfo();

    // Extract tracking parameters from current URL
    const trackingParams =
      typeof window !== "undefined"
        ? extractTrackingParams(new URLSearchParams(window.location.search))
        : {};

    await fetch("/api/user-action", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        eventType,
        questionId,
        value: Array.isArray(value) ? value.join(",") : value,
        userId,
        sessionId,
        engagementTime,
        // Include tracking parameters
        trackingParams,
      }),
    });
  } catch {}
}

export async function sendPageView({
  page,
  pageTitle,
  firstSource,
}: {
  page?: string;
  pageTitle?: string;
  firstSource?: string;
}) {
  try {
    const { userId, sessionId, engagementTime } = getSessionInfo();

    // Extract tracking parameters from current URL
    const trackingParams =
      typeof window !== "undefined"
        ? extractTrackingParams(new URLSearchParams(window.location.search))
        : {};

    await fetch("/api/user-action", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        eventType: "client_page_view",
        page,
        pageTitle,
        firstSource,
        userId,
        sessionId,
        engagementTime,
        // Include tracking parameters in pageview
        trackingParams,
      }),
    });
  } catch {}
}

export async function recordRecommendationDisplay({
  recommendationId,
  recommendationName,
}: {
  recommendationId: string;
  recommendationName: string;
}) {
  try {
    const { userId, sessionId, engagementTime } = getSessionInfo();

    // Send display event to GA4 via our API
    await fetch("/api/user-action", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        eventType: "recommendation_display",
        recommendationId,
        recommendationName,
        userId,
        sessionId,
        engagementTime,
      }),
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error recording recommendation display:", error);
  }
}

export async function recordRecommendationClick({
  recommendationId,
  recommendationName,
  actionId,
  actionName,
  actionUrl,
  isCta,
}: {
  recommendationId: string;
  recommendationName: string;
  actionId: string;
  actionName: string;
  actionUrl: string;
  isCta: boolean;
}) {
  try {
    const { userId, sessionId, engagementTime } = getSessionInfo();

    // Create URL with custom tracking parameters (not blocked by adblockers)
    const trackedUrl = addTrackingParameters(actionUrl, {
      source: "crowdsec_onboarding",
      medium: "recommendation",
      campaign: "kickstart_wizard",
      content: `${recommendationId}_${actionId}`,
      term: isCta ? "cta" : "secondary_action",
    });

    // Send to GA4 via our API
    await fetch("/api/user-action", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        eventType: "recommendation_click",
        recommendationId,
        recommendationName,
        actionId,
        actionName,
        actionUrl: trackedUrl, // Send tracking-enhanced URL
        isCta,
        userId,
        sessionId,
        engagementTime,
      }),
    });

    // Return the tracking-enhanced URL for opening
    return trackedUrl;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error processing recommendation click:", error);

    return actionUrl; // Return original URL if processing fails
  }
}
